import type { PlaylistDocument, PlaylistEntry, PlaylistFormat, ProTVTarget, SerializeOptions } from "./types";

function linesWithPrefix(value: string, prefix: string): string[] {
  return value
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => `${prefix}${line}`);
}

function mainUrlFor(entry: PlaylistEntry, prefix: string): string {
  return `${prefix}${entry.mainUrl}`;
}

function serializeTokenized(document: PlaylistDocument, options: SerializeOptions): string {
  const chunks: string[] = [];
  const prefix = options.urlPrefix?.trim() ?? "";

  if (document.header.trim()) chunks.push(document.header.trim());

  for (const entry of document.entries) {
    const lines = [`@${mainUrlFor(entry, prefix)}`];

    if (options.target !== "2.2" && entry.alternateUrl) lines.push(`^${entry.alternateUrl}`);
    if (options.includeImagePaths !== false && entry.imagePath) lines.push(`/${entry.imagePath.replace(/^\/+/, "")}`);
    if (options.target !== "2.2" && entry.tags.length > 0) lines.push(`#${entry.tags.join(",")}`);

    if (options.target === "3.x") {
      lines.push(...linesWithPrefix(entry.title, "~"));
      if (entry.description) lines.push(...entry.description.split(/\r?\n/));
    } else if (entry.title) {
      lines.push(...entry.title.split(/\r?\n/));
    }

    chunks.push(lines.join("\n"));
  }

  return `${chunks.join("\n\n")}\n`;
}

function serializeJson(document: PlaylistDocument, options: SerializeOptions): string {
  const prefix = options.urlPrefix?.trim() ?? "";
  return `${JSON.stringify(
    {
      header: document.header,
      entries: document.entries.map((entry) => ({
        mainUrl: mainUrlFor(entry, prefix),
        alternateUrl: entry.alternateUrl,
        title: entry.title,
        description: entry.description,
        tags: entry.tags.join(","),
        image: options.includeImagePaths === false ? "" : entry.imagePath,
      })),
    },
    null,
    2,
  )}\n`;
}

export function effectiveFormat(target: ProTVTarget, requested: PlaylistFormat): PlaylistFormat {
  return target === "3.x" ? requested : "tokenized";
}

export function serializePlaylist(document: PlaylistDocument, options: SerializeOptions): string {
  const format = effectiveFormat(options.target, options.format);
  return format === "json" ? serializeJson(document, options) : serializeTokenized(document, options);
}

export function normalizePlaylistFilename(value: string): string {
  const withoutControlCharacters = [...value]
    .map((character) => (character.charCodeAt(0) < 32 ? "-" : character))
    .join("");
  const base = withoutControlCharacters
    .trim()
    .replace(/\.playlist$/i, "")
    .replace(/[<>:"/\\|?*]/g, "-")
    .replace(/\s+/g, " ")
    .replace(/[. ]+$/g, "")
    .slice(0, 120);
  return `${base || "ProTV-Playlist"}.playlist`;
}
